import { stripQuery } from '@apps/web/lib/strip_query';

type DatadogRumRuntimeConfig = {
    readonly runEnvironment: 'development' | 'staging' | 'production';
    readonly sha: string;
    readonly datadogRumApplicationId: string | null;
    readonly datadogRumClientToken: string | null;
    readonly datadogSite: string | null;
};

let initializationPromise: Promise<void> | null = null;

// Real User Monitoring. We use this to measure per-country / per-endpoint
// real-user latency (e.g. Philippines vs US). RUM geo-locates sessions
// server-side and, with resource tracking on, records client-observed timing
// for every fetch/XHR.
export function initializeDatadogRum(
    config: DatadogRumRuntimeConfig,
): Promise<void> {
    const applicationId = config.datadogRumApplicationId?.trim();
    const clientToken = config.datadogRumClientToken?.trim();
    // No-op when unconfigured so dev/staging/preview (null tokens) stay quiet;
    // RUM only activates where both values are present (production).
    if (!applicationId || !clientToken) {
        return Promise.resolve();
    }

    if (!initializationPromise) {
        initializationPromise = import('@datadog/browser-rum').then(
            ({ datadogRum }) => {
                datadogRum.init({
                    applicationId,
                    clientToken,
                    site: config.datadogSite?.trim() || 'datadoghq.com',
                    service: 'web',
                    env: config.runEnvironment,
                    version: config.sha,
                    // RUM bills each kept session, and latency percentiles
                    // stay representative at a fifth of sessions.
                    sessionSampleRate: 20,
                    // Session Replay is intentionally off: it adds cost and
                    // captures PII. We only want performance telemetry.
                    sessionReplaySampleRate: 0,
                    // Records per-resource (fetch/XHR) timing, which is what
                    // gives us client-observed latency per endpoint.
                    trackResources: true,
                    trackLongTasks: true,
                    // Click/interaction tracking is intentionally off: RUM
                    // derives action names from the clicked element's text,
                    // which can be a client/caregiver name in our tables, and
                    // we don't need click telemetry for latency. Disabling it
                    // removes that PHI vector entirely.
                    trackUserInteractions: false,
                    defaultPrivacyLevel: 'mask-user-input',
                    // `allowedTracingUrls` is intentionally unset. Setting it
                    // makes RUM inject `x-datadog-*` headers into API calls,
                    // which would require api.phoebe.work to allow them via
                    // CORS. Leaving it unset keeps zero CORS risk; APM trace
                    // correlation can be a fast-follow once CORS is confirmed.
                    beforeSend: (event) => {
                        // `defaultPrivacyLevel` does not scrub resource/view
                        // URLs, so strip query strings before any event is
                        // sent to keep name-bearing search terms out of
                        // Datadog while preserving the endpoint path.
                        event.view.url = stripQuery(event.view.url);
                        if (event.view.referrer) {
                            event.view.referrer = stripQuery(
                                event.view.referrer,
                            );
                        }
                        if (event.type === 'resource') {
                            event.resource.url = stripQuery(event.resource.url);
                        }
                        return true;
                    },
                });
            },
        );
    }

    return initializationPromise;
}
